import {
  ActivityStatusEnum,
  ActivityTypeEnum,
  cancelSubActivity,
  claimSubActivity,
  enableSubActivity,
  queryActivityQueue,
  TUserActivity,
} from '@/apis/promotions';
import MoneyWithCurrencyIcon from '@/components/biz/MoneyWithCurrencyIcon';
import BonusCountDown from '@/components/biz/rewards/bonusCountDown';
import CircleProgress from '@/components/biz/rewards/circleProgress';
import InfoTooltip from '@/components/ui/common/InfoTooltip';
import WrappedButton from '@/components/ui/WrappedButton';
import useTheme from '@/theme/useTheme';
import toPercent from '@/utils/number/toPercent';
import { Paper, Stack, Typography } from '@mui/material';
import { useRequest } from 'ahooks';
import { confirm } from 'material-ui-confirm';
import { Trans, useTranslation } from 'next-i18next';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';
import toast from 'react-hot-toast';
import FreeSpinBlock from '../components/free-spin-block';
import reloadSwapBanner from '../assets/reload_swap_banner.png';
import { TermsAndConditionsLink } from '@/components/features/common/TermsAndConditions';
import { ChevronRight, Close } from '@mui/icons-material';
import queryKeysFactory from '@/reactQueryKeys/keysFactory';
import { useQuery } from '@tanstack/react-query';
import ThursdayTAndC from '@/components/biz/promotions/weekly/thursday-t-and-c';
import { ThursdayOrSaturdayTAndC } from '@/components/biz/promotions/weekly/helpers';

const ActivityCardItem: React.FC<{
  activity: TUserActivity;
}> = ({ activity }) => {
  const { status, activityTitle, endTime, activityType } = activity;
  const { theme } = useTheme();
  const [t] = useTranslation('rewards');
  const { push } = useRouter();

  const { refetch } = useQuery(queryKeysFactory.rewards.activityQueue());

  const handleToast = (data: any) => {
    if (data?.success) {
      toast.success(t('Success'));
    } else {
      toast.error(data?.message);
    }
    refetch();
  };

  const { runAsync: enableActivity, loading: loadingEnable } = useRequest(
    () => {
      return enableSubActivity(activity.recordId);
    },
    {
      manual: true,
      onSuccess: handleToast,
    },
  );

  const { runAsync: cancelActivity, loading: loadingCancel } = useRequest(
    () => {
      return cancelSubActivity(activity.recordId);
    },
    {
      manual: true,
      onSuccess: handleToast,
    },
  );

  const { runAsync: claimActivity, loading: loadingClaim } = useRequest(
    () => {
      return claimSubActivity(activity.recordId);
    },
    {
      manual: true,
      onSuccess: handleToast,
    },
  );

  let bonusDetail;
  let bonusHeader;

  if (activityType === ActivityTypeEnum.RELOAD) {
    const hasClaimableBonus = activity.sequences.some((s) => s.status === 1);
    bonusDetail =
      status === ActivityStatusEnum.FINISHED ? (
        <Image src={reloadSwapBanner.src} alt="banner" fill />
      ) : (
        <Stack padding={3}>
          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
          >
            <Typography
              variant="caption"
              display={'flex'}
              alignItems={'center'}
              color={'primary.main'}
            >
              <Link href={'/benefits/deposit#reload-bonus'}>
                {t('rewards::Bonus Details')}{' '}
              </Link>
              <ChevronRight fontSize="small"></ChevronRight>
            </Typography>
            <WrappedButton
              size="small"
              variant="text"
              endIcon={<Close></Close>}
              onClick={() => {
                confirm({
                  title: t('Cancel Bonus'),
                  description: (
                    <Trans
                      i18nKey={'cancel_bonus'}
                      defaults={
                        'You have selected to cancel this bonus. Cancelling this bonus will remove the bonus entirely. You will not get it back or reactivate the current bonus. <br></br> You have <money></money> unclaimed bonus. This will be transferred once you confirmed the cancellation. Are you sure you want to cancel?'
                      }
                      components={{
                        br: <br />,
                        money: (
                          <MoneyWithCurrencyIcon
                            value={activity.availableBonusAmount ?? 0}
                          />
                        ),
                      }}
                    ></Trans>
                  ),
                  confirmationText: t('Yes'),
                  cancellationText: t('No'),
                })
                  .then(() => {
                    cancelActivity();
                  })
                  .catch(() => {
                    // cancel
                  });
                // toast.error(t('Please claim your bonus first'));
              }}
              disabled={status === ActivityStatusEnum.WAIT}
              isLoading={loadingCancel}
            >
              {t('Cancel')}
            </WrappedButton>
          </Stack>
          <Stack direction={['column', 'row']} gap={2}>
            {(status === ActivityStatusEnum.NOT_VALID ||
              status === ActivityStatusEnum.CANCELED ||
              status === ActivityStatusEnum.WAIT) && (
              <Stack
                sx={{
                  position: 'absolute',
                  background: 'rgba(24, 26, 32, 0.80)',
                  left: 0,
                  top: 0,
                  width: '100%',
                  height: '100%',
                  zIndex: 1,
                }}
                alignItems={'center'}
                justifyContent={'center'}
              >
                <Trans
                  i18nKey={
                    status === ActivityStatusEnum.NOT_VALID
                      ? 'Complete your Freespin to unlock bonus. Please check the <terms>Terms & Conditions</terms> for any obtained with the help of free spins'
                      : status === ActivityStatusEnum.CANCELED
                        ? 'This bonus has been canceled'
                        : `<button>Get Bonus</button>`
                  }
                  components={{
                    button: (
                      <WrappedButton
                        onClick={enableActivity}
                        isLoading={loadingEnable}
                        sx={{
                          width: '80%',
                        }}
                      />
                    ),
                    terms: (
                      <ThursdayOrSaturdayTAndC
                        type={ActivityTypeEnum.THURSDAY}
                      />
                    ),
                  }}
                ></Trans>
              </Stack>
            )}

            <Stack gap={2}>
              <Stack>
                <Typography>
                  {t('rewards::Deposit')}:{' '}
                  <MoneyWithCurrencyIcon value={activity.depositAmount} />
                </Typography>
                <Typography>
                  {t('rewards::Bonus')}:{' '}
                  <MoneyWithCurrencyIcon value={activity.totalBonusAmount} />
                </Typography>
                <Typography display={'flex'} alignItems={'center'}>
                  {t('rewards::Wager Contributed')}
                  <InfoTooltip
                    text={
                      <Trans i18nKey="Wager contributed during this Welcome Bonus. Game Category Wagering Contribution % can be found in Bonus Details." />
                    }
                  />
                  :&nbsp;
                  <MoneyWithCurrencyIcon value={activity.currentWagerAmount} />
                  &nbsp;(
                  {toPercent(
                    Number(activity.currentWagerAmount) /
                      Number(activity.targetWagerAmount),
                  )}
                  )
                </Typography>
              </Stack>
              <Typography fontSize={12}>
                {t(
                  'rewards::Wager 5x the deposit amount you made to active the deposit bonus to unlock 10% of the bonus!',
                )}
              </Typography>
              <Stack
                gap={1}
                direction={['column', 'row']}
                sx={{
                  '> *': {
                    width: '100%',
                  },
                }}
              >
                {status === 0 ? (
                  <WrappedButton
                    isLoading={loadingEnable}
                    onClick={enableActivity}
                    sx={{
                      paddingY: [theme.spacing(1), theme.spacing(2)],
                      height: 'auto',
                    }}
                  >
                    {t('reward::Get Bonus')}
                  </WrappedButton>
                ) : (
                  <WrappedButton
                    sx={{
                      paddingY: [theme.spacing(1), theme.spacing(2)],
                      height: 'auto',
                    }}
                    onClick={() =>
                      hasClaimableBonus ? claimActivity() : push('/casino')
                    }
                    isLoading={loadingClaim}
                  >
                    {hasClaimableBonus ? (
                      <Trans
                        i18nKey="claim_bonus"
                        defaults="Claim Available Bonus <money></money>"
                        ns="reward"
                        components={{
                          money: (
                            <span
                              style={{
                                marginInlineStart: theme.spacing(1),
                              }}
                            >
                              <MoneyWithCurrencyIcon
                                value={activity.availableBonusAmount ?? 0}
                              />
                            </span>
                          ),
                        }}
                      />
                    ) : (
                      t('reward::Play Games')
                    )}
                  </WrappedButton>
                )}

                {activity.status !== ActivityStatusEnum.FINISHED && (
                  <BonusCountDown
                    countdown={activity.endTime}
                    titleText={t('rewards::Deposit Bonus Expire in')}
                  />
                )}
              </Stack>
            </Stack>
            <Stack gap={2} flexGrow={1}>
              <Stack direction={'row'} justifyContent={'space-between'}>
                <Typography>{t('You Claimed')}:</Typography>
                <Typography>
                  <MoneyWithCurrencyIcon value={activity.claimedAmount} />
                </Typography>
              </Stack>
              <Stack direction={'row'} justifyContent={'space-between'}>
                <Typography>{t('Available Bonus')}:</Typography>
                <Typography>
                  <MoneyWithCurrencyIcon
                    value={activity.availableBonusAmount}
                  />
                </Typography>
              </Stack>
              <Stack
                display={'grid'}
                gridTemplateColumns={'repeat(5, auto)'}
                gridTemplateRows={'2'}
                justifyContent={'space-between'}
                gap={1}
              >
                <CircleProgress
                  wagerRequired={Number(activity.targetWagerAmount)}
                  currentWagered={Number(activity.currentWagerAmount)}
                  claimedIndex={activity.claimedTimes - 1}
                />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      );
    bonusHeader = (
      <Typography
        padding={1}
        display={'inline'}
        color={theme.palette.grey['1000']}
        sx={{
          background: theme.palette.primary.light,
          alignSelf: 'flex-start',
          borderTopRightRadius: theme.spacing(1),
        }}
      >
        {status === ActivityStatusEnum.FINISHED ? (
          <>{t('MoonClub Rewards')}</>
        ) : (
          <>
            {t('Thursday Reload Bonus')}&nbsp;(
            {(Number(activity.totalBonusAmount) /
              Number(activity.depositAmount) || 1) * 100}
            %)
          </>
        )}
      </Typography>
    );
  } else {
    bonusDetail = <FreeSpinBlock activity={activity} refresh={refetch} />;
  }

  return (
    <Stack flex={1}>
      {/* {bonusHeader} */}
      <Paper
        sx={{
          background: (theme) => theme.palette.bg.secondary,
          position: 'relative',
          flex: activityType === ActivityTypeEnum.RELOAD ? 1 : undefined,
        }}
      >
        {bonusDetail}
      </Paper>
    </Stack>
  );
};

export default ActivityCardItem;
